import { COMMON_CONSTANTS } from '../../../../store/oneButtonStore';
import type { ColumnsType } from 'antd/es/table';

function normalizeFinancePartnerKey(partner: string): string {
    return (partner || '').replace(/\s/g, '').toLowerCase();
}

export function isLightreachSolarFinancePartner(opportunityDetails: any | null | undefined): boolean {
    const products = opportunityDetails?.OpportunityProduct || [];
    const solar = products.find((p: any) => p.Product_Type_Name === 'Solar' && p.ActiveFlag);
    if (!solar) return false;
    const partner = String(solar.Finance_Partner_Display_Name || solar.Finance_Partner || '');
    return (
        normalizeFinancePartnerKey(partner) ===
        normalizeFinancePartnerKey(COMMON_CONSTANTS.LightreachPartnerName)
    );
}

/** Show LightReach eligible-products tooling when Solar is on the deal and LightReach is the solar finance partner. */
export function shouldShowLightreachEligibleProducts(
    opportunityDetails: any | null | undefined,
): boolean {
    return !!(
        opportunityDetails?.OpportunityInterest?.Solar &&
        isLightreachSolarFinancePartner(opportunityDetails)
    );
}

export function getLightreachLeadIdFromOpportunity(od: any): string | null {
    if (!od) return null;
    const list =
        od.FinancePartnerLeadList ??
        od.financePartnerLeadListModels ??
        od.financePartnerLeadList ??
        [];
    if (Array.isArray(list)) {
        for (const row of list) {
            const fp = String(row.FinancePartnerName ?? row.Finance_Partner ?? '').toLowerCase();
            if (!fp.includes('lightreach')) continue;
            const pt = String(row.ProductTypeName ?? row.Product_Type_Name ?? '').toLowerCase();
            if (pt.includes('solar')) {
                const id = row.Finance_Partner_Sync_LeadID ?? row.FinancePartnerSyncLeadId;
                if (id) return String(id);
            }
        }
        for (const row of list) {
            const fp = String(row.FinancePartnerName ?? row.Finance_Partner ?? '').toLowerCase();
            if (fp.includes('lightreach')) {
                const id = row.Finance_Partner_Sync_LeadID ?? row.FinancePartnerSyncLeadId;
                if (id) return String(id);
            }
        }
    }
    const top = od.Finance_Partner_Sync_LeadID ?? od.OpportunityDetails?.Finance_Partner_Sync_LeadID;
    return top != null && String(top).trim() !== '' ? String(top) : null;
}

export function normalizeLightreachPricingRows(raw: unknown): Record<string, unknown>[] {
    if (Array.isArray(raw)) return raw as Record<string, unknown>[];
    if (raw && typeof raw === 'object') {
        const o = raw as Record<string, unknown>;
        if (Array.isArray(o.Data)) return o.Data as Record<string, unknown>[];
        if (Array.isArray(o.eligibleProductDetails)) return o.eligibleProductDetails as Record<string, unknown>[];
    }
    return [];
}

export function sortLightreachEligibleRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
    return [...rows].sort((a, b) => {
        const escA = Number(a.EscalationRate);
        const escB = Number(b.EscalationRate);
        const preferA = escA === 0.0299 ? 0 : 1;
        const preferB = escB === 0.0299 ? 0 : 1;
        if (preferA !== preferB) return preferA - preferB;
        const kwhA = Number(a.KwhRate) || 0;
        const kwhB = Number(b.KwhRate) || 0;
        if (kwhA !== kwhB) return kwhA - kwhB;
        return (Number(a.EscalationRate) || 0) - (Number(b.EscalationRate) || 0);
    });
}

function fmtUsdCell(n: unknown): string {
    const x = Number(n);
    if (Number.isNaN(x)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(x);
}

function fmtUsdCellPreservePrecision(n: unknown): string {
    if (n == null || n === '') return '—';
    const x = Number(n);
    if (Number.isNaN(x)) return String(n);
    const raw = String(n).trim();
    const fracLen = raw.includes('.') ? raw.split('.')[1]?.length ?? 0 : 0;
    const maxFractionDigits = Math.min(Math.max(fracLen, 2), 10);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: maxFractionDigits,
    }).format(x);
}

function fmtEscalationCell(v: unknown): string {
    if (v == null || v === '') return '—';
    const x = Number(v);
    if (Number.isNaN(x)) return String(v);
    if (x > 0 && x <= 1) return `${(x * 100).toFixed(2)}%`;
    return `${x}%`;
}

function lrSortTextField(field: string) {
    return (a: Record<string, unknown>, b: Record<string, unknown>) =>
        String(a[field] ?? '').localeCompare(String(b[field] ?? ''), undefined, { numeric: true, sensitivity: 'base' });
}

function lrSortNumericField(field: string) {
    return (a: Record<string, unknown>, b: Record<string, unknown>) => {
        const na = Number(a[field]);
        const nb = Number(b[field]);
        const va = Number.isFinite(na) ? na : 0;
        const vb = Number.isFinite(nb) ? nb : 0;
        return va - vb;
    };
}

export const LIGHTREACH_ELIGIBLE_COLUMNS: ColumnsType<Record<string, unknown>> = [
    {
        title: 'Type',
        dataIndex: 'Type',
        key: 'Type',
        width: 100,
        sorter: lrSortTextField('Type'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Description',
        dataIndex: 'Description',
        key: 'Description',
        ellipsis: true,
        sorter: lrSortTextField('Description'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'KWH Rate',
        dataIndex: 'KwhRate',
        key: 'KwhRate',
        width: 110,
        render: (v: unknown) => fmtUsdCellPreservePrecision(v),
        sorter: lrSortNumericField('KwhRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'PPW Rate',
        dataIndex: 'PpwRate',
        key: 'PpwRate',
        width: 110,
        render: (v: unknown) => fmtUsdCell(v),
        sorter: lrSortNumericField('PpwRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Escalation Rate',
        dataIndex: 'EscalationRate',
        key: 'EscalationRate',
        width: 120,
        render: (v: unknown) => fmtEscalationCell(v),
        sorter: lrSortNumericField('EscalationRate'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Program Type',
        dataIndex: 'ProgramType',
        key: 'ProgramType',
        width: 120,
        sorter: lrSortTextField('ProgramType'),
        sortDirections: ['ascend', 'descend'],
    },
    {
        title: 'Total EPC Payment',
        dataIndex: 'TotalEpcPayment',
        key: 'TotalEpcPayment',
        width: 140,
        render: (v: unknown) => fmtUsdCell(v),
        sorter: lrSortNumericField('TotalEpcPayment'),
        sortDirections: ['ascend', 'descend'],
    },
];
